
// Cookie consent management utility

// Types of tracking that require consent
export type ConsentType = 'necessary' | 'analytics' | 'marketing';

export interface ConsentPreferences {
  necessary: boolean; // Always true, can't be disabled
  analytics: boolean;
  marketing: boolean;
}

// Default preferences
const defaultPreferences: ConsentPreferences = {
  necessary: true, // Always required
  analytics: false,
  marketing: false,
};

// Local storage key
const CONSENT_STORAGE_KEY = 'cookie-consent-preferences';
const CONSENT_SHOWN_KEY = 'cookie-consent-shown';

// Get current consent preferences
export const getConsentPreferences = (): ConsentPreferences => {
  try {
    const stored = localStorage.getItem(CONSENT_STORAGE_KEY);
    if (stored) {
      return JSON.parse(stored);
    }
  } catch (error) {
    console.error('Error reading consent preferences:', error);
  }
  return { ...defaultPreferences };
};

// Check if user has given consent for a specific type
export const hasConsent = (type: ConsentType): boolean => {
  if (type === 'necessary') return true; // Always allow necessary
  const preferences = getConsentPreferences();
  return preferences[type] === true;
};

// Save consent preferences
export const saveConsentPreferences = (preferences: ConsentPreferences): void => {
  try {
    // Ensure necessary cookies are always accepted
    const updatedPreferences = { 
      ...preferences,
      necessary: true 
    };
    
    localStorage.setItem(CONSENT_STORAGE_KEY, JSON.stringify(updatedPreferences));
    
    // Mark banner as shown
    localStorage.setItem(CONSENT_SHOWN_KEY, 'true');
    
    // Update Google consent if present
    if (typeof window !== 'undefined' && window.dataLayer) {
      // Push consent event to dataLayer for GTM
      window.dataLayer.push({
        event: 'consent_update',
        analytics_consent: updatedPreferences.analytics ? 'granted' : 'denied',
        marketing_consent: updatedPreferences.marketing ? 'granted' : 'denied'
      });
      
      // Update Google Analytics consent mode
      if (window.gtag) {
        window.gtag('consent', 'update', {
          analytics_storage: updatedPreferences.analytics ? 'granted' : 'denied',
          ad_storage: updatedPreferences.marketing ? 'granted' : 'denied',
        });
      }
    }
  } catch (error) {
    console.error('Error saving consent preferences:', error);
  }
};

// Accept all cookies
export const acceptAllCookies = (): void => {
  saveConsentPreferences({
    necessary: true,
    analytics: true,
    marketing: true,
  });
};

// Accept only necessary cookies
export const acceptNecessaryCookies = (): void => {
  saveConsentPreferences({
    necessary: true,
    analytics: false,
    marketing: false,
  });
};

// Check if consent banner has been shown/actioned
export const hasConsentBeenGiven = (): boolean => {
  try {
    return localStorage.getItem(CONSENT_SHOWN_KEY) === 'true';
  } catch (error) {
    return false;
  }
};
